import React, { useState, useEffect, useMemo, useRef } from 'react';
import './DataTable.css';

const DataTable = ({ data, title, onTableSwitch, availableTables = [] }) => {
  const [globalSearch, setGlobalSearch] = useState('');
  const [columnSearches, setColumnSearches] = useState({});
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(50);
  const [parsedData, setParsedData] = useState({ headers: [], rows: [] });
  const [visibleColumns, setVisibleColumns] = useState({});
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const columnSelectorRef = useRef(null);

  // Parse TSV/CSV data
  useEffect(() => {
    if (!data || typeof data !== 'string') {
      setParsedData({ headers: [], rows: [] });
      setVisibleColumns({});
      return;
    }

    const lines = data.trim().split('\n');
    if (lines.length === 0) {
      setParsedData({ headers: [], rows: [] });
      setVisibleColumns({});
      return;
    }

    const headers = lines[0].split('\t');
    const rows = lines.slice(1).map(line => {
      const cells = line.split('\t');
      // Ensure we have the right number of columns
      while (cells.length < headers.length) {
        cells.push('');
      }
      return cells;
    });

    setParsedData({ headers, rows });
    
    // Initialize all columns as visible by default
    const initialVisibility = {};
    headers.forEach((_, index) => {
      initialVisibility[index] = true;
    });
    setVisibleColumns(initialVisibility);
    
    setColumnSearches({});
    setCurrentPage(0);
  }, [data]);

  // Get filtered columns based on visibility
  const visibleColumnIndices = useMemo(() => {
    return Object.entries(visibleColumns)
      .filter(([_, isVisible]) => isVisible)
      .map(([index, _]) => parseInt(index));
  }, [visibleColumns]);

  // Filter data based on searches
  const filteredData = useMemo(() => {
    if (!parsedData.rows.length) return [];

    return parsedData.rows.filter(row => {
      // Global search - search only visible columns
      const globalMatch = globalSearch === '' || 
        visibleColumnIndices.some(colIndex => {
          const cell = row[colIndex] || '';
          return cell.toLowerCase().includes(globalSearch.toLowerCase());
        });

      if (!globalMatch) return false;

      // Column-specific searches
      const columnMatch = Object.entries(columnSearches).every(([colIndex, searchTerm]) => {
        if (!searchTerm) return true;
        const cellValue = row[parseInt(colIndex)] || '';
        return cellValue.toLowerCase().includes(searchTerm.toLowerCase());
      });

      return columnMatch;
    });
  }, [parsedData.rows, globalSearch, columnSearches, visibleColumnIndices]);

  // Paginated data
  const paginatedData = useMemo(() => {
    const startIndex = currentPage * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, pageSize]);

  // Calculate smart column widths to prevent horizontal scroll
  const columnWidths = useMemo(() => {
    const visibleCount = visibleColumnIndices.length;
    if (visibleCount === 0) return [];
    
    // Adjust widths based on visible column count
    if (visibleCount <= 3) {
      return Array(visibleCount).fill(33);
    } else if (visibleCount <= 6) {
      return Array(visibleCount).fill(16.66);
    } else if (visibleCount <= 10) {
      return Array(visibleCount).fill(10);
    } else {
      return Array(visibleCount).fill(8);
    }
  }, [visibleColumnIndices]);

  // Handle column search change
  const handleColumnSearchChange = (columnIndex, value) => {
    setColumnSearches(prev => ({
      ...prev,
      [columnIndex]: value
    }));
    setCurrentPage(0);
  };

  // Handle global search change
  const handleGlobalSearchChange = (value) => {
    setGlobalSearch(value);
    setCurrentPage(0);
  };

  // Handle column visibility toggle
  const toggleColumnVisibility = (columnIndex) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnIndex]: !prev[columnIndex]
    }));
    setCurrentPage(0);
  };

  // Show/hide all columns
  const toggleAllColumns = (visible) => {
    const newVisibility = {};
    parsedData.headers.forEach((_, index) => {
      newVisibility[index] = visible;
    });
    setVisibleColumns(newVisibility);
    setCurrentPage(0);
  };

  // Click outside handler for column selector
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnSelectorRef.current && !columnSelectorRef.current.contains(event.target)) {
        setShowColumnSelector(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load more data (pagination)
  const loadMore = () => {
    const nextPage = currentPage + 1;
    const maxPage = Math.ceil(filteredData.length / pageSize) - 1;
    if (nextPage <= maxPage) {
      setCurrentPage(nextPage);
    }
  };

  // Calculate display info
  const startItem = currentPage * pageSize + 1;
  const endItem = Math.min((currentPage + 1) * pageSize, filteredData.length);
  const totalItems = filteredData.length;

  // Infinite scroll handler
  useEffect(() => {
    const tableWrapper = document.querySelector('.WSNG-data-table-wrapper');
    if (!tableWrapper) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = tableWrapper;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px threshold
      
      if (isAtBottom && endItem < totalItems) {
        loadMore();
      }
    };

    tableWrapper.addEventListener('scroll', handleScroll);
    return () => tableWrapper.removeEventListener('scroll', handleScroll);
  }, [endItem, totalItems]);

  if (!parsedData.headers.length) {
    return (
      <div className="WSNG-data-table-empty">
        <p>No data to display</p>
      </div>
    );
  }

  return (
    <div className="WSNG-data-table-container">
      {/* Header with table switcher, column selector, and global search */}
      <div className="WSNG-data-table-header">
        <div className="WSNG-data-table-title-section">
          <h3 className="WSNG-data-table-title">{title}</h3>
          {availableTables.length > 1 && (
            <div className="WSNG-table-switcher">
              {availableTables.map((table, index) => (
                <button
                  key={index}
                  className={`WSNG-table-switch-btn ${table.active ? 'active' : ''}`}
                  onClick={() => onTableSwitch && onTableSwitch(table)}
                >
                  {table.name}
                </button>
              ))}
            </div>
          )}
        </div>
        
        <div className="WSNG-data-table-controls">
          {/* Column Selector */}
          <div className="WSNG-column-selector" ref={columnSelectorRef}>
            <button 
              className="WSNG-column-selector-btn"
              onClick={() => setShowColumnSelector(!showColumnSelector)}
              title="Select visible columns"
            >
              <i className="fas fa-columns"></i>
              <span>Columns ({visibleColumnIndices.length}/{parsedData.headers.length})</span>
            </button>
            
            {showColumnSelector && (
              <div className="WSNG-column-dropdown">
                <div className="WSNG-column-dropdown-header">
                  <span>Select Columns to Display</span>
                  <div className="WSNG-column-dropdown-actions">
                    <button onClick={() => toggleAllColumns(true)} className="WSNG-select-all-btn">All</button>
                    <button onClick={() => toggleAllColumns(false)} className="WSNG-select-none-btn">None</button>
                  </div>
                </div>
                <div className="WSNG-column-list">
                  {parsedData.headers.map((header, index) => (
                    <label key={index} className="WSNG-column-checkbox-item">
                      <input
                        type="checkbox"
                        checked={visibleColumns[index] || false}
                        onChange={() => toggleColumnVisibility(index)}
                      />
                      <span className="WSNG-column-checkbox-label">{header}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Global Search */}
          <div className="WSNG-global-search">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Search all columns..."
              value={globalSearch}
              onChange={(e) => handleGlobalSearchChange(e.target.value)}
              className="WSNG-global-search-input"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="WSNG-data-table-wrapper">
        <table className="WSNG-data-table">
          <thead>
            <tr>
              {visibleColumnIndices.map((columnIndex, displayIndex) => (
                <th key={columnIndex} style={{ width: `${columnWidths[displayIndex]}%` }} className="WSNG-data-table-th">
                  <div className="WSNG-column-header">
                    <span className="WSNG-column-title">{parsedData.headers[columnIndex]}</span>
                    <input
                      type="text"
                      placeholder="Filter..."
                      value={columnSearches[columnIndex] || ''}
                      onChange={(e) => handleColumnSearchChange(columnIndex, e.target.value)}
                      className="WSNG-column-search"
                    />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, rowIndex) => (
              <tr key={rowIndex} className="WSNG-data-table-row">
                {visibleColumnIndices.map((columnIndex, displayIndex) => (
                  <td key={columnIndex} style={{ width: `${columnWidths[displayIndex]}%` }} className="WSNG-data-table-td">
                    <div className="WSNG-cell-content" title={row[columnIndex]}>
                      {row[columnIndex]}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer with pagination info only */}
      <div className="WSNG-data-table-footer">
        <div className="WSNG-data-table-info">
          Showing {startItem}-{endItem} of {totalItems} items
          {visibleColumnIndices.length < parsedData.headers.length && (
            <span className="WSNG-hidden-columns-info"> • {parsedData.headers.length - visibleColumnIndices.length} columns hidden</span>
          )}
          {endItem < totalItems && <span className="WSNG-loading-indicator"> • Loading more when you scroll...</span>}
        </div>
      </div>
    </div>
  );
};

export default DataTable;
